import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dices, Layers, X } from 'lucide-react';

import { useActiveMatchContext } from '@/contexts/ActiveMatchContext';
import { useAppDispatch } from '@/store/hooks';
import { clearActiveMatch } from '@/store/activeMatch.slice';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

/**
 * Docked above the TabBar when a real-time match is live and the user is NOT on
 * its match screen. Tapping the body returns to the (still-connected) match;
 * the X resigns (with confirm). The socket lives in ActiveMatchProvider, so
 * navigating here never dropped the connection — no forfeit.
 */
export function ActiveMatchPill() {
  const { t } = useTranslation('activeMatch');
  const { descriptor, projection, resignActive } = useActiveMatchContext();
  const location = useLocation();
  const navigate = useNavigate();
  const haptics = useHaptics();
  const dispatch = useAppDispatch();
  const [confirming, setConfirming] = useState(false);

  if (!descriptor) return null;

  const matchPath =
    descriptor.game === 'qgammon'
      ? `/backgammon/match/${descriptor.matchId}`
      : `/chinesepoker/match/${descriptor.matchId}`;
  // Hide while already on the match screen.
  if (location.pathname === matchPath) return null;

  const Icon = descriptor.game === 'qgammon' ? Dices : Layers;
  const label = descriptor.game === 'qgammon' ? t('backgammon') : t('chinesePoker');
  const status =
    projection?.phase === 'yourTurn'
      ? t('yourTurn')
      : projection?.phase === 'ended'
        ? t('ended')
        : t('waiting');

  const goToMatch = () => {
    haptics.selection();
    navigate(matchPath);
  };

  const doResign = () => {
    haptics.notification('warning');
    resignActive();
    dispatch(clearActiveMatch());
    setConfirming(false);
  };

  return (
    <div className="mx-3 mb-1 flex items-center gap-2 rounded-2xl border border-brand/40 bg-brand/10 px-3 py-2">
      <button
        type="button"
        onClick={goToMatch}
        className="flex min-w-0 flex-1 items-center gap-2.5 text-left transition-opacity active:opacity-80"
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand/15 text-brand">
          <Icon aria-hidden className="h-4 w-4" strokeWidth={2.5} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-fg">{label}</span>
          <span
            className={cn(
              'block truncate text-xs',
              projection?.phase === 'yourTurn' ? 'text-brand' : 'text-fg-muted',
            )}
          >
            {status}
          </span>
        </span>
      </button>
      {confirming ? (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={doResign}
            className="rounded-lg bg-danger/15 px-2.5 py-1 text-xs font-semibold text-danger"
          >
            {t('confirmResign')}
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="rounded-lg px-2 py-1 text-xs font-semibold text-fg-muted"
          >
            {t('cancel')}
          </button>
        </div>
      ) : (
        <button
          type="button"
          aria-label={t('resign')}
          onClick={() => {
            haptics.selection();
            setConfirming(true);
          }}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-fg-muted transition-opacity active:opacity-70"
        >
          <X aria-hidden className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
